/* ============================================================
   ManMade motion — springs + rubber-band overscroll
   Interactive panels, sliders, accordions and press states run on damped
   springs (position + velocity, retargeted mid-flight) instead of
   fixed-duration CSS transitions, so grabbing something mid-motion never
   restarts it. Drag gestures are touch-only; mouse and keyboard drive the
   same springs through the normal controls.
   Exposes window.MMSpring. No dependencies.
   ============================================================ */
(function () {
  'use strict';
  if (window.MMSpring) return;

  /* Presets — start here, tune by feel. Floaty: raise stiffness first.
     Wobbly: raise damping first. Never fix it with a duration. */
  var PRESETS = {
    toggle:    { stiffness: 500, damping: 32 },
    press:     { stiffness: 500, damping: 30 },
    accordion: { stiffness: 350, damping: 32 },
    drawer:    { stiffness: 300, damping: 32 },
    modal:     { stiffness: 300, damping: 30 },
    header:    { stiffness: 350, damping: 35 },
    slider:    { stiffness: 300, damping: 30 }
  };

  /* Rubber-band constant C as a fraction of the viewport size d.
     1.0 feels ~1:1 at first, then decays. Test 0.5–1.0; smaller = stiffer. */
  var RUBBER = 1;

  var rmq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(rmq && rmq.matches); }

  function extend(a, b) { var o = {}, k; for (k in a) o[k] = a[k]; for (k in b) o[k] = b[k]; return o; }
  function clamp(x, lo, hi) { return x < lo ? lo : x > hi ? hi : x; }
  function vpSize(axis) { return axis === 'x' ? window.innerWidth : window.innerHeight; }

  /* ----------------------------------------------------------
     1. SPRING — one per animated value
     ---------------------------------------------------------- */
  function create(o) {
    var k = o.stiffness, c = o.damping, m = o.mass || 1;
    var eps = o.precision || 0.01;
    var x = o.from || 0, v = 0, target = x, raf = 0, last = 0;

    function emit() { if (o.onUpdate) o.onUpdate(x, v); }
    function settle() { x = target; v = 0; raf = 0; emit(); if (o.onRest) o.onRest(x); }
    function atRest() { return Math.abs(x - target) < eps && Math.abs(v) < eps * 20; }

    function frame(now) {
      var dt = last ? clamp((now - last) / 1000, 0, 0.064) : 1 / 60;
      last = now;
      // F = -k(x - target) - c*v ; a = F / m — fixed 1/120 s substeps keep it stable at any refresh rate
      var n = Math.max(1, Math.ceil(dt * 120)), h = dt / n;
      for (var i = 0; i < n; i++) {
        v += ((-k * (x - target) - c * v) / m) * h;
        x += v * h;
      }
      if (atRest()) return settle();
      emit();
      raf = requestAnimationFrame(frame);
    }

    function stop() { if (raf) cancelAnimationFrame(raf); raf = 0; }

    return {
      value: function () { return x; },
      target: function () { return target; },
      moving: function () { return !!raf; },
      // Jump with no motion — initial state, and following a finger during a drag
      set: function (val) { stop(); x = target = val; v = 0; emit(); },
      setVelocity: function (vel) { v = vel; },
      // Move position AND target by d, keeping velocity — for looping carousels
      // that jump between identical clone/real slides without a visible change
      shift: function (d) { x += d; target += d; emit(); },
      // The normal path: retarget, keeping the current position and velocity
      goTo: function (t, vel) {
        target = t;
        if (vel !== undefined) v = vel;
        if (reduced()) { stop(); return settle(); }
        if (raf) return;
        if (atRest()) return settle();
        last = 0;
        raf = requestAnimationFrame(frame);
      },
      stop: stop
    };
  }

  /* ----------------------------------------------------------
     2. RUBBER BAND — hyperbola d·x / (x + C)
     ~1:1 at first, decays, and can never pass d (one viewport).
     ---------------------------------------------------------- */
  function rubber(x, d) { return d * x / (x + d * RUBBER); }
  function unrubber(y, d) { return y >= d ? 1e6 : d * RUBBER * y / (d - y); }
  function band(x, d) { return x < 0 ? -rubber(-x, d) : rubber(x, d); }
  function unband(y, d) { return y < 0 ? -unrubber(-y, d) : unrubber(y, d); }
  // d(band)/dx at a displayed offset — converts finger velocity into band velocity
  function bandSlope(y, d) { var c = d * RUBBER, ex = unrubber(Math.abs(y), d); return d * c / ((ex + c) * (ex + c)); }

  /* ----------------------------------------------------------
     3. TOUCH DRAG — axis lock, one owner per touch
     Inner handlers see the move first (bubbling), so the innermost
     element whose canStart() accepts the gesture owns it.
     ---------------------------------------------------------- */
  var owner = null;
  document.addEventListener('touchstart', function () { owner = null; }, { capture: true, passive: true });

  function velocityTracker() {
    var pts = [];
    return {
      reset: function () { pts.length = 0; },
      add: function (p) {
        var t = performance.now();
        pts.push([t, p]);
        while (pts.length > 2 && t - pts[0][0] > 100) pts.shift();
      },
      // px/s over the last ~100 ms; 0 if the finger rested before lifting
      get: function () {
        if (pts.length < 2) return 0;
        var a = pts[0], b = pts[pts.length - 1];
        if (performance.now() - b[0] > 80 || b[0] === a[0]) return 0;
        return (b[1] - a[1]) / ((b[0] - a[0]) / 1000);
      }
    };
  }

  function findTouch(list, id) {
    for (var i = 0; i < list.length; i++) if (list[i].identifier === id) return list[i];
    return null;
  }

  function drag(el, o) {
    var axis = o.axis, id = null, sx = 0, sy = 0, decided = false, active = false;
    var vel = velocityTracker();

    function finish() {
      id = null;
      if (!active) return;
      active = false;
      o.onEnd(vel.get());
    }

    el.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { finish(); return; }
      var t = e.touches[0];
      id = t.identifier; sx = t.clientX; sy = t.clientY;
      decided = false; active = false;
      vel.reset();
    }, { passive: true });

    el.addEventListener('touchmove', function (e) {
      if (id === null || (decided && !active)) return;
      var t = findTouch(e.changedTouches, id);
      if (!t) return;
      if (!decided) {
        var dx = t.clientX - sx, dy = t.clientY - sy;
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        decided = true;
        var along = axis === 'x' ? dx : dy, across = axis === 'x' ? dy : dx;
        if (owner || !e.cancelable || Math.abs(along) <= Math.abs(across) || !o.canStart(along, e.target)) return;
        owner = el; active = true;
        sx = t.clientX; sy = t.clientY; // measure from here so nothing jumps by the slop
        o.onStart();
      }
      e.preventDefault();
      var p = axis === 'x' ? t.clientX : t.clientY;
      vel.add(p);
      o.onMove(p - (axis === 'x' ? sx : sy));
    }, { passive: false });

    function end(e) { if (id !== null && findTouch(e.changedTouches, id)) finish(); }
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
  }

  // True when something between node and root can still scroll natively in the
  // direction the finger is moving — that element should get the gesture instead.
  function canScroll(node, root, axis, along) {
    for (; node && node.nodeType === 1; node = node.parentNode) {
      var max = axis === 'x' ? node.scrollWidth - node.clientWidth : node.scrollHeight - node.clientHeight;
      if (max > 1) {
        var ov = getComputedStyle(node)[axis === 'x' ? 'overflowX' : 'overflowY'];
        if (ov === 'auto' || ov === 'scroll') {
          var pos = axis === 'x' ? node.scrollLeft : node.scrollTop;
          if (along > 0 ? pos > 0.5 : pos < max - 0.5) return true;
        }
      }
      if (node === root) break;
    }
    return false;
  }

  /* ----------------------------------------------------------
     4. PANEL — drawers and bottom sheets
     One px spring: 0 = open, size = closed. Dragging toward the closed
     side follows the finger; past fully open it rubber-bands. Release
     hands the flick velocity to the spring.
     opts: side ('left'|'right'|'bottom'), scrim, preset, open,
           onDismiss (drag released closed), onClosed (settled closed)
     ---------------------------------------------------------- */
  function panel(el, o) {
    var axis = o.side === 'bottom' ? 'y' : 'x';
    var dir = o.side === 'left' ? -1 : 1; // on-screen sign of a closing move
    var scrim = o.scrim || null;
    var open = !!o.open, size = 1, raw0 = 0, baseShadow = null, fill = '';

    function measure() {
      size = (axis === 'x' ? el.offsetWidth : el.offsetHeight) || 1;
      // Past fully open, paint the panel colour into the gap left at its anchored edge
      var cs = getComputedStyle(el), half = size / 2;
      if (baseShadow === null) baseShadow = cs.boxShadow && cs.boxShadow !== 'none' ? cs.boxShadow + ', ' : '';
      fill = baseShadow + (axis === 'x' ? dir * half + 'px 0' : '0 ' + half + 'px') + ' 0 0 ' + cs.backgroundColor;
    }

    var s = create(extend(o.preset || PRESETS.drawer, {
      precision: 0.5,
      onUpdate: function (x) {
        el.style.transform = (axis === 'x' ? 'translateX(' : 'translateY(') + x * dir + 'px)';
        el.style.boxShadow = x < -0.5 ? fill : '';
        if (scrim) scrim.style.opacity = clamp(1 - x / size, 0, 1);
      },
      onRest: function () {
        // Hand the resting state back to the stylesheet ([data-open] / .is-open)
        el.style.transform = '';
        el.style.boxShadow = '';
        if (scrim) scrim.style.opacity = '';
        if (!open && o.onClosed) o.onClosed();
      }
    }));

    drag(el, {
      axis: axis,
      canStart: function (along, target) { return open && !canScroll(target, el, axis, along); },
      onStart: function () {
        s.stop();
        measure();
        raw0 = unband(Math.min(s.value(), 0), vpSize(axis)) + Math.max(s.value(), 0);
      },
      onMove: function (d) {
        var raw = raw0 + d * dir;
        s.set(raw < 0 ? band(raw, vpSize(axis)) : Math.min(raw, size));
      },
      onEnd: function (v) {
        v *= dir;
        s.setVelocity(v);
        if (v > 500 || (v > -500 && s.value() > size * 0.4)) {
          if (o.onDismiss) o.onDismiss(); else api.close();
        } else {
          s.goTo(0);
        }
      }
    });

    var api = {
      open: function () {
        if (open) return;
        open = true;
        measure();
        if (!s.moving()) s.set(size); // from closed — unless mid-close, then just reverse
        s.goTo(0);
      },
      close: function () {
        if (!open) return;
        open = false;
        measure();
        s.goTo(size);
      },
      isOpen: function () { return open; }
    };
    return api;
  }

  /* ----------------------------------------------------------
     5. MODAL — centred dialogs
     Fades/scales on a 0..1 spring; swipe down to dismiss on a second
     (px) spring, so the flick velocity carries into the exit.
     opts: scrim, open, onDismiss, onClosed
     ---------------------------------------------------------- */
  function modal(el, o) {
    var scrim = o.scrim || null;
    var open = !!o.open, h = 1, raw0 = 0;
    var p, y;

    function render() {
      var pv = p.value(), yv = y.value(), f = clamp(pv, 0, 1);
      el.style.opacity = f;
      el.style.transform = 'translateY(' + ((1 - pv) * 12 + yv) + 'px) scale(' + (0.985 + 0.015 * pv) + ')';
      if (scrim) scrim.style.opacity = f * clamp(1 - yv / h, 0, 1);
    }
    function rest() {
      if (p.moving() || y.moving()) return;
      if (!open) y.set(0);
      el.style.opacity = '';
      el.style.transform = '';
      if (scrim) scrim.style.opacity = '';
      if (!open && o.onClosed) o.onClosed();
    }

    p = create(extend(o.preset || PRESETS.modal, { from: open ? 1 : 0, precision: 0.001, onUpdate: render, onRest: rest }));
    y = create(extend(o.preset || PRESETS.modal, { precision: 0.5, onUpdate: render, onRest: rest }));

    drag(el, {
      axis: 'y',
      canStart: function (along, target) { return open && !canScroll(target, el, 'y', along); },
      onStart: function () {
        y.stop();
        h = el.offsetHeight || 1;
        raw0 = unband(Math.min(y.value(), 0), window.innerHeight) + Math.max(y.value(), 0);
      },
      onMove: function (d) {
        var raw = raw0 + d;
        y.set(raw < 0 ? band(raw, window.innerHeight) : raw);
      },
      onEnd: function (v) {
        y.setVelocity(v);
        if (v > 500 || (v > -500 && y.value() > h * 0.25)) {
          if (o.onDismiss) o.onDismiss(); else api.close();
        } else {
          y.goTo(0);
        }
      }
    });

    var api = {
      open: function () {
        if (open) return;
        open = true;
        h = el.offsetHeight || 1;
        if (!p.moving() && !y.moving()) { y.set(0); p.set(0); }
        p.goTo(1);
        y.goTo(0);
      },
      close: function () {
        if (!open) return;
        open = false;
        p.goTo(0);
        // Dragged down: keep travelling the way the finger threw it while it fades
        if (y.value() > 0.5) y.goTo(y.value() + window.innerHeight * 0.5);
      },
      isOpen: function () { return open; }
    };
    return api;
  }

  /* ----------------------------------------------------------
     6. REVEAL — sticky header / bars sliding along y
     Position is clamped to its two ends: these must never bounce.
     opts: shown, offset() → hidden translateY in px, preset
     ---------------------------------------------------------- */
  function reveal(el, o) {
    var shown = !!o.shown;
    var s = create(extend(o.preset || PRESETS.header, {
      from: shown ? 0 : o.offset(),
      precision: 0.5,
      onUpdate: function (x) {
        var hid = o.offset();
        el.style.transform = 'translateY(' + (hid < 0 ? clamp(x, hid, 0) : clamp(x, 0, hid)) + 'px)';
      },
      onRest: function () { el.style.transform = ''; }
    }));
    return {
      show: function () { if (shown) return; shown = true; s.goTo(0); },
      hide: function () { if (!shown) return; shown = false; s.goTo(o.offset()); }
    };
  }

  /* ----------------------------------------------------------
     7. ACCORDIONS — <details>
     Height still snaps (layout is never animated); the content fades and
     slides on a spring and icons follow via --mm-acc (0..1). Closing waits
     for the spring, so a second tap mid-close reverses it from where it is.
     ---------------------------------------------------------- */
  var ACCORDIONS = '.mm-faq details, .mm-hfaq__item, .mm-sheet__faq, .mm-cart__promo, .mm-cart__upsells, .mm-buybox__faq-item';
  var EXCLUSIVE = '[data-mm-faq-exclusive], [data-mm-home-faq]';

  function accordionSpring(d) {
    if (d.__mmAcc) return d.__mmAcc;
    function parts() {
      return Array.prototype.filter.call(d.children, function (c) { return c.tagName !== 'SUMMARY'; });
    }
    d.__mmAcc = create(extend(PRESETS.accordion, {
      from: d.open ? 1 : 0,
      precision: 0.001,
      onUpdate: function (p) {
        var f = clamp(p, 0, 1), t = 'translateY(' + (1 - p) * -8 + 'px)';
        d.style.setProperty('--mm-acc', p);
        parts().forEach(function (c) { c.style.opacity = f; c.style.transform = t; });
      },
      onRest: function (p) {
        d.style.removeProperty('--mm-acc');
        parts().forEach(function (c) { c.style.opacity = ''; c.style.transform = ''; });
        if (!p) d.open = false;
      }
    }));
    return d.__mmAcc;
  }

  function accordion(d, wantOpen) {
    var s = accordionSpring(d);
    if (wantOpen) {
      if (!d.open) { s.set(0); d.open = true; }
      s.goTo(1);
    } else if (d.open) {
      s.goTo(0);
    }
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    var sum = e.target.closest && e.target.closest('summary');
    var d = sum && sum.parentNode;
    if (!d || d.tagName !== 'DETAILS' || !d.matches(ACCORDIONS) || d.querySelector('summary') !== sum) return;
    e.preventDefault();
    var wantOpen = !(d.open && accordionSpring(d).target() === 1);
    var group = wantOpen && d.closest(EXCLUSIVE);
    if (group) {
      group.querySelectorAll('details').forEach(function (other) { if (other !== d && other.open) accordion(other, false); });
    }
    accordion(d, wantOpen);
  });

  /* ----------------------------------------------------------
     8. BUTTON PRESS — scale to 0.97 while held, spring back on release
     ---------------------------------------------------------- */
  var PRESSABLE = '.mm-btn, .mm-hero__endcard-reel';

  document.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 || reduced()) return;
    var b = e.target.closest && e.target.closest(PRESSABLE);
    if (!b || b.disabled) return;
    var s = b.__mmPress || (b.__mmPress = create(extend(PRESETS.press, {
      from: 1,
      precision: 0.0005,
      onUpdate: function (x) { b.style.transform = 'scale(' + x + ')'; },
      onRest: function (x) { if (x === 1) b.style.transform = ''; }
    })));
    s.goTo(0.97);
    function release() {
      s.goTo(1);
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
    }
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
  }, { passive: true });

  /* ----------------------------------------------------------
     9. OVERSCROLL — rubber band past the ends of a scroll container
     Touch only. The browser's own bounce is switched off on the element so
     the two never fight; mid-range scrolling stays fully native. Once a pull
     is claimed the whole touch is ours, so reversing scrolls the content by hand.
     opts: always — band even when the content fits, like an iOS list
           fling  — a native fling that hits an end carries its speed into the
                    band instead of stopping dead
           pin    — children that stay put (bars); static ones are lifted while
                    banded so the content slides under them
     ---------------------------------------------------------- */
  var FLING_MIN = 300, FLING_MAX = 4000; // px/s — slower hits just stop; the cap keeps the bounce under ~95px

  function overscroll(el, axis, o) {
    if (el.__mmOverscroll) return;
    el.__mmOverscroll = true;
    o = o || {};
    el.style[axis === 'x' ? 'overscrollBehaviorX' : 'overscrollBehaviorY'] = 'none';

    var max = 0, v0 = 0, kids = [], lift = [];
    var s = create(extend(PRESETS.slider, {
      precision: 0.5,
      onUpdate: function (x) {
        var t = x ? (axis === 'x' ? 'translateX(' : 'translateY(') + x + 'px)' : '';
        for (var i = 0; i < kids.length; i++) kids[i].style.transform = t;
        for (i = 0; i < lift.length; i++) { lift[i].style.position = x ? 'relative' : ''; lift[i].style.zIndex = x ? '1' : ''; }
      }
    }));
    function pos() { return axis === 'x' ? el.scrollLeft : el.scrollTop; }
    function setPos(p) { if (axis === 'x') el.scrollLeft = p; else el.scrollTop = p; }
    function span() { return Math.max(0, axis === 'x' ? el.scrollWidth - el.clientWidth : el.scrollHeight - el.clientHeight); }
    function grab() {
      kids = []; lift = [];
      for (var c = el.firstElementChild; c; c = c.nextElementSibling) {
        if (!o.pin || !c.matches(o.pin)) kids.push(c);
        else if (getComputedStyle(c).position === 'static') lift.push(c);
      }
    }

    if (o.fling) {
      // Touch momentum only (not wheel or code). The impact frame is clipped by
      // the edge, so take the faster of it and the frame before.
      var down = false, upAt = -1e9, lp = 0, lt = 0, lv = 0;
      var up = function (e) { if (!e.touches.length) { down = false; upAt = performance.now(); } };
      el.addEventListener('touchstart', function () { down = true; }, { passive: true });
      el.addEventListener('touchend', up, { passive: true });
      el.addEventListener('touchcancel', up, { passive: true });
      el.addEventListener('scroll', function () {
        var now = performance.now(), p = pos(), dt = now - lt;
        var v = lt && dt > 0 && dt < 100 ? (p - lp) * 1000 / dt : 0;
        var hit = v * lv > 0 && Math.abs(lv) > Math.abs(v) ? lv : v;
        lp = p; lt = now; lv = v;
        if (down || now - upAt > 3000 || s.moving()) return;
        if (!(p <= 0 && hit < -FLING_MIN) && !(p >= span() - 0.5 && hit > FLING_MIN)) return;
        grab();
        s.goTo(0, -clamp(hit, -FLING_MAX, FLING_MAX));
      }, { passive: true });
    }

    drag(el, {
      axis: axis,
      canStart: function (along, target) {
        max = span();
        if (max < 1 && !o.always) return false; // not scrollable — nothing to band
        if (s.moving()) return true;          // catch it mid spring-back
        return !canScroll(target, el, axis, along); // only at an edge, pulling outward
      },
      onStart: function () {
        grab();
        var off = s.value(), d = vpSize(axis);
        s.stop();
        // Virtual scroll position: below 0 or above max while the content is banded
        v0 = off > 0 ? -unrubber(off, d) : off < 0 ? max + unrubber(-off, d) : pos();
      },
      onMove: function (delta) {
        var v = v0 - delta, d = vpSize(axis);
        if (v < 0) { setPos(0); s.set(rubber(-v, d)); }
        else if (v > max) { setPos(max); s.set(-rubber(v - max, d)); }
        else { setPos(v); if (s.value()) s.set(0); }
      },
      onEnd: function (vel) {
        var x = s.value();
        if (x) s.goTo(0, vel * bandSlope(x, vpSize(axis)));
      }
    });
  }

  // Drawers bounce like an iOS sheet: always, flings included; the nav menu's
  // top panel and frosted bottom bar stay put while the list moves.
  // Sideways strips band their own cards at the ends. Without this a pull past
  // the last card of a strip inside the cart drawer (the add-on rail, the
  // reviews row) falls through to the drawer panel, which drags the whole
  // drawer sideways.
  var DRAWER = { always: true, fling: true };
  var OVERSCROLL = [
    ['.mm-cart__items', 'y', DRAWER],
    ['.mm-drawer', 'y', extend(DRAWER, { pin: '.mm-drawer__head, .mm-drawer__foot' })],
    ['.mm-vc__track, .mm-gv__track, .mm-vproof__tiles, .mm-hero__thumbs, .mm-cart__addons-list, .mm-cart__rv-track', 'x']
  ];
  function attachOverscroll() {
    OVERSCROLL.forEach(function (r) {
      document.querySelectorAll(r[0]).forEach(function (el) { overscroll(el, r[1], r[2]); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attachOverscroll);
  else attachOverscroll();

  window.MMSpring = {
    PRESETS: PRESETS,
    reduced: reduced,
    create: create,
    rubber: rubber,
    unrubber: unrubber,
    bandSlope: bandSlope,
    drag: drag,
    canScroll: canScroll,
    panel: panel,
    modal: modal,
    reveal: reveal,
    accordion: accordion,
    overscroll: overscroll
  };
})();
